import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Building2, 
  Phone, 
  Mail, 
  MapPin,
  X,
  Save,
  Loader2,
  CreditCard,
  Landmark
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN'
  }).format(amount || 0);
};

// Supplier Form Component
const SupplierForm = ({ supplier, businessId, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    business_name: '',
    rfc: '',
    bank_info: {
      bank_name: '',
      account_number: '',
      clabe: ''
    },
    address: {
      street: '',
      exterior_number: '',
      interior_number: '',
      neighborhood: '',
      postal_code: '',
      city: '',
      state: ''
    },
    primary_contact: {
      name: '',
      email: '',
      phone: '',
      position: ''
    },
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [neighborhoods, setNeighborhoods] = useState([]);

  useEffect(() => {
    if (supplier) {
      setFormData({
        business_name: supplier.business_name || '',
        rfc: supplier.rfc || '',
        bank_info: supplier.bank_info || { bank_name: '', account_number: '', clabe: '' },
        address: supplier.address || { street: '', exterior_number: '', interior_number: '', neighborhood: '', postal_code: '', city: '', state: '' },
        primary_contact: supplier.primary_contact || { name: '', email: '', phone: '', position: '' },
        notes: supplier.notes || ''
      });
    }
  }, [supplier]);

  const lookupPostalCode = async (cp) => {
    if (cp.length !== 5) return;
    try {
      const response = await axios.get(`${API}/postal-codes/${cp}`);
      if (response.data.success) {
        setFormData(prev => ({
          ...prev,
          address: {
            ...prev.address,
            city: (response.data.city || '').toUpperCase(),
            state: (response.data.state || '').toUpperCase()
          }
        }));
        // Colonias también en mayúsculas
        setNeighborhoods((response.data.neighborhoods || []).map(n => n.toUpperCase()));
      }
    } catch (error) {
      console.error('Error looking up postal code:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.business_name.trim()) {
      toast.error("La razón social es requerida");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        business_id: businessId,
        ...formData
      };

      if (supplier?.id) {
        await axios.put(`${API}/suppliers/${supplier.id}`, payload);
        toast.success("Proveedor actualizado");
      } else {
        await axios.post(`${API}/suppliers`, payload);
        toast.success("Proveedor creado");
      }
      onSave();
    } catch (error) {
      console.error('Error saving supplier:', error);
      toast.error("Error al guardar proveedor");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="glass-card rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-white flex items-center gap-2">
          <Building2 className="w-5 h-5 text-orange-500" />
          {supplier?.id ? 'Editar Proveedor' : 'Nuevo Proveedor'}
        </h3>
        <button onClick={onCancel} className="p-2 text-slate-400 hover:text-white">
          <X className="w-5 h-5" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Datos Fiscales */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-slate-400 uppercase tracking-wider">Datos Fiscales</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Razón Social *</label>
              <input
                type="text"
                value={formData.business_name}
                onChange={(e) => setFormData({...formData, business_name: e.target.value.toUpperCase()})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none"
                placeholder="NOMBRE DE LA EMPRESA SA DE CV"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">RFC</label>
              <input
                type="text"
                value={formData.rfc}
                onChange={(e) => setFormData({...formData, rfc: e.target.value.toUpperCase()})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none"
                placeholder="ABC123456XY9"
                maxLength={13}
              />
            </div>
          </div>
        </div>

        {/* Información Bancaria */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-slate-400 uppercase tracking-wider flex items-center gap-2">
            <Landmark className="w-4 h-4" />
            Información Bancaria
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Banco</label>
              <input
                type="text"
                value={formData.bank_info.bank_name}
                onChange={(e) => setFormData({...formData, bank_info: {...formData.bank_info, bank_name: e.target.value.toUpperCase()}})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none"
                placeholder="BANORTE"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Número de Cuenta</label>
              <input
                type="text"
                value={formData.bank_info.account_number}
                onChange={(e) => setFormData({...formData, bank_info: {...formData.bank_info, account_number: e.target.value}})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none"
                placeholder="1234567890"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">CLABE Interbancaria</label>
              <input
                type="text"
                value={formData.bank_info.clabe}
                onChange={(e) => setFormData({...formData, bank_info: {...formData.bank_info, clabe: e.target.value}})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none"
                placeholder="012345678901234567"
                maxLength={18}
              />
            </div>
          </div>
        </div>

        {/* Dirección */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-slate-400 uppercase tracking-wider flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            Dirección
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm text-slate-400 mb-1">Calle</label>
              <input
                type="text"
                value={formData.address.street}
                onChange={(e) => setFormData({...formData, address: {...formData.address, street: e.target.value.toUpperCase()}})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">No. Ext</label>
              <input
                type="text"
                value={formData.address.exterior_number}
                onChange={(e) => setFormData({...formData, address: {...formData.address, exterior_number: e.target.value}})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">No. Int</label>
              <input
                type="text"
                value={formData.address.interior_number}
                onChange={(e) => setFormData({...formData, address: {...formData.address, interior_number: e.target.value}})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">C.P.</label>
              <input
                type="text"
                value={formData.address.postal_code}
                onChange={(e) => {
                  const cp = e.target.value.replace(/\D/g, '').slice(0, 5);
                  setFormData({...formData, address: {...formData.address, postal_code: cp}});
                  if (cp.length === 5) lookupPostalCode(cp);
                }}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none"
                maxLength={5}
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Colonia</label>
              {neighborhoods.length > 0 ? (
                <select
                  value={formData.address.neighborhood}
                  onChange={(e) => setFormData({...formData, address: {...formData.address, neighborhood: e.target.value}})}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none"
                >
                  <option value="">Seleccionar...</option>
                  {neighborhoods.map((n, i) => (
                    <option key={i} value={n}>{n}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={formData.address.neighborhood}
                  onChange={(e) => setFormData({...formData, address: {...formData.address, neighborhood: e.target.value.toUpperCase()}})}
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none"
                />
              )}
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Ciudad</label>
              <input
                type="text"
                value={formData.address.city}
                onChange={(e) => setFormData({...formData, address: {...formData.address, city: e.target.value.toUpperCase()}})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Estado</label>
              <input
                type="text"
                value={formData.address.state}
                onChange={(e) => setFormData({...formData, address: {...formData.address, state: e.target.value.toUpperCase()}})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Contacto Principal */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-slate-400 uppercase tracking-wider">Contacto Principal</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Nombre</label>
              <input
                type="text"
                value={formData.primary_contact.name}
                onChange={(e) => setFormData({...formData, primary_contact: {...formData.primary_contact, name: e.target.value.toUpperCase()}})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none uppercase"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Cargo</label>
              <input
                type="text"
                value={formData.primary_contact.position}
                onChange={(e) => setFormData({...formData, primary_contact: {...formData.primary_contact, position: e.target.value.toUpperCase()}})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none uppercase"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Email</label>
              <input
                type="email"
                value={formData.primary_contact.email}
                onChange={(e) => setFormData({...formData, primary_contact: {...formData.primary_contact, email: e.target.value}})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Teléfono</label>
              <input
                type="tel"
                value={formData.primary_contact.phone}
                onChange={(e) => setFormData({...formData, primary_contact: {...formData.primary_contact, phone: e.target.value}})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Notas */}
        <div>
          <label className="block text-sm text-slate-400 mb-1">Notas</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({...formData, notes: e.target.value})}
            rows={2}
            className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none resize-none"
          />
        </div>

        {/* Botones */}
        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-orange-500 text-white rounded-lg flex items-center gap-2 hover:bg-orange-600 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {supplier?.id ? 'Actualizar' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  );
};

// Supplier Card Component
const SupplierCard = ({ supplier, onEdit, onDelete }) => {
  return (
    <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4 hover:bg-slate-800/50 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center">
            <Building2 className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h4 className="font-semibold text-white">{supplier.business_name}</h4>
            <p className="text-sm text-slate-400">{supplier.rfc || 'Sin RFC'}</p>
            <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-slate-500">
              {supplier.bank_info?.bank_name && (
                <span className="flex items-center gap-1">
                  <Landmark className="w-3 h-3" />
                  {supplier.bank_info.bank_name}
                </span>
              )}
              {supplier.primary_contact?.phone && (
                <span className="flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  {supplier.primary_contact.phone}
                </span>
              )}
              {supplier.primary_contact?.email && (
                <span className="flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  {supplier.primary_contact.email}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onEdit(supplier)}
            className="p-1.5 text-slate-400 hover:bg-slate-700 rounded transition-colors"
            title="Editar"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(supplier)}
            className="p-1.5 text-red-400 hover:bg-red-500/20 rounded transition-colors"
            title="Eliminar"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Component
export default function SuppliersTab({ businessId }) {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);

  const fetchSuppliers = async () => {
    try {
      // Proveedores son GLOBALES - no filtrar por business_id
      const response = await axios.get(`${API}/suppliers`);
      setSuppliers(response.data.suppliers || []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      toast.error("Error al cargar proveedores");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();
  }, []); // Sin dependencia de businessId porque son globales

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setShowForm(true);
  };

  const handleDelete = async (supplier) => {
    if (!window.confirm(`¿Eliminar proveedor "${supplier.business_name}"?`)) return;
    
    try {
      await axios.delete(`${API}/suppliers/${supplier.id}`);
      toast.success("Proveedor eliminado");
      fetchSuppliers();
    } catch (error) {
      console.error('Error deleting supplier:', error);
      toast.error("Error al eliminar proveedor");
    }
  };

  const handleSave = () => {
    setShowForm(false);
    setEditingSupplier(null);
    fetchSuppliers();
  };

  const filteredSuppliers = suppliers.filter(s => 
    s.business_name?.toLowerCase().includes(search.toLowerCase()) ||
    s.rfc?.toLowerCase().includes(search.toLowerCase()) ||
    s.primary_contact?.name?.toLowerCase().includes(search.toLowerCase())
  );

  if (showForm) {
    return (
      <SupplierForm
        supplier={editingSupplier}
        businessId={businessId}
        onSave={handleSave}
        onCancel={() => { setShowForm(false); setEditingSupplier(null); }}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Proveedores</h2>
          <p className="text-slate-400 text-sm">{suppliers.length} proveedores registrados</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-orange-500 text-white rounded-lg flex items-center gap-2 hover:bg-orange-600 transition-colors"
          data-testid="new-supplier-btn"
        >
          <Plus className="w-4 h-4" />
          Nuevo Proveedor
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <input
          type="text"
          placeholder="Buscar por nombre, RFC o contacto..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none"
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      ) : filteredSuppliers.length === 0 ? (
        <div className="text-center py-12">
          <Building2 className="w-12 h-12 mx-auto text-slate-600 mb-3" />
          <p className="text-slate-400">
            {search ? 'No se encontraron proveedores' : 'No hay proveedores registrados'}
          </p>
          {!search && (
            <button
              onClick={() => setShowForm(true)}
              className="mt-4 text-orange-400 hover:text-orange-300"
            >
              Agregar primer proveedor
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-3">
          {filteredSuppliers.map((supplier) => (
            <SupplierCard
              key={supplier.id}
              supplier={supplier}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}
